import { OriginEntityType } from '@raptor-fe/core';
import { NamedRoute, NamedRoutes } from '@raptor-fe/services';

/**
 * Context needed to build a navigable origin route: the tenant/branch the caller is in plus the
 * origin record's id. Assembled by the caller from ApplicationContextStore and the message's
 * `originEntityId`.
 */
export interface OriginRouteContext {
    tenantId: string;
    branchId: string;
    id: string;
}

/**
 * Extensible origin-type -> route-factory map for the message origin link (Spec #76394). A message
 * stamped with an origin renders a small link icon that navigates to that origin's detail screen.
 * Only `Inspection` is wired this phase -- it resolves to the real dvi inspection-detail route
 * (`NamedRoutes.inspectionDetail`, `/app/dvi/inspection/details` with tenant/branch/id query params).
 * `WorkOrder` and `Quote` origins are persisted but have no detail screen yet, so they are absent
 * here and NO icon renders for them; add an entry when their screens exist -- no component change
 * needed. This replaces the mockup's static `/view-inspection` placeholder.
 */
export const ORIGIN_ROUTES: Partial<Record<OriginEntityType, (context: OriginRouteContext) => NamedRoute<object>>> = {
    Inspection: (context) => NamedRoutes.inspectionDetail(context),
};

/** True when the origin type has a wired destination and should therefore surface a link icon. */
export function hasOriginRoute(type: OriginEntityType | null): boolean {
    return !!type && !!ORIGIN_ROUTES[type];
}

/** The route for an origin, or null when the type has no wired destination yet. */
export function originRoute(type: OriginEntityType | null, context: OriginRouteContext): NamedRoute<object> | null {
    if (!type) {
        return null;
    }
    const build = ORIGIN_ROUTES[type];
    return build ? build(context) : null;
}
